import {
  EdgesType,
  NodesType,
  NodesTypeData,
  NodeTypes,
} from './EditProcessMap';
import { outputCellNodeStyles } from './OutputCellNode';

export type NodeResponse = {
  nodeId: string;
  processId: string;
  name: string;
  isRepeatable: boolean;
  hasInput: boolean;
  positionX: number;
  positionY: number;
  icon: string;
  description: string;
  isEndOfFlow: boolean;
  isInitOfFlow?: boolean;
  status: string;
  outputCellCount: number;
  type: string;
  assignedUserCount: number;
};

type EdgeResponse = {
  edgeId: string;
  sourceNodeId: string;
  targetNodeId: string;
  connectionType: string;
};

export const transformFlowData = (
  rawNodes: NodesType[],
  rawEdges: EdgesType[]
) => {
  // Transform nodes
  const nodes = rawNodes.map(node => {
    // check if the node has an loop edge
    const isRepeatable = rawEdges.some(
      edge => edge.source === node.id && edge.source === edge.target
    );

    // check if the node is any edge's source
    const hasInput = rawEdges.some(edge => edge.source === node.id);

    return {
      nodeId: node.id.toUpperCase(),
      processId: (node.data as NodesTypeData).processId || '',
      name: (node.data as NodesTypeData).processName || '',
      description: (node.data as NodesTypeData).processDescription,
      isEndOfFlow: (node.data as NodesTypeData).isEndOfFlow || false,
      positionX: node.position.x,
      positionY: node.position.y,
      isRepeatable,
      hasInput,
      type: node.data.type,
    };
  });

  // Transform edges
  const edges = rawEdges.map(edge => ({
    edgeId: edge.id.toUpperCase(),
    sourceNodeId: edge.source,
    targetNodeId: edge.target,
    connectionType: edge.type === 'selfLoopEdge' ? 'loop' : 'normal',
  }));

  return { nodes, edges };
};

/**
 * Parses backend nodes into the format required for React Flow.
 * @param nodes - Array of nodes received from the backend.
 * @param handleDeleteNode - Function to handle node deletion.
 * @param processMapId - The ID of the process map, coming from the URL or context.
 * @returns Nodes ready for React Flow.
 */
export const parseNodes = (
  nodes: NodeResponse[],
  edges: EdgeResponse[],
  handleDeleteNode: (nodeId: string) => void,
  processMapId: string
): NodesType[] => {
  return nodes?.map(node => {
    // we should add a type attribute in the db, this criteria is not good
    const isIntermediate = node.type === NodeTypes.OUTPUT_INPUT_CELL;

    let connectedSource: string | undefined;
    let connectedTarget: string | undefined;

    if (isIntermediate) {
      const incomingEdge = edges.find(e => e.targetNodeId === node.nodeId);
      const outgoingEdge = edges.find(e => e.sourceNodeId === node.nodeId);

      connectedSource = incomingEdge?.sourceNodeId;
      connectedTarget = outgoingEdge?.targetNodeId;
    }

    return {
      id: node.nodeId,
      position: {
        x: node.positionX,
        y: node.positionY,
      },
      data: {
        newNodeId: node.nodeId,
        processId: node.processId,
        processName: node.name,
        processDescription: node.description,
        processIcon: node.icon,
        processStatus: node.status,
        assignedUserCount: node.assignedUserCount,
        processMapId,
        isSaved: true,
        type: node.type as NodeTypes,
        isDragging: false,
        isEndOfFlow: node.isEndOfFlow,
        isInitOfFlow: node.isInitOfFlow,
        onDelete: () => handleDeleteNode(node.nodeId),
        ...(isIntermediate && {
          connectedSource,
          connectedTarget,
          outputCellValue: node.outputCellCount,
        }),
      },
      type: isIntermediate ? 'intermediateNode' : 'customNode',
      ...(isIntermediate && { style: outputCellNodeStyles }),
    };
  });
};

/**
 * Parses edges from the backend into the format required for React Flow.
 * @param edges - Array of edges received from the backend.
 * @param setEdges - Function to update the edges when one is deleted.
 * @returns Edges ready for React Flow.
 */
export const parseEdges = (
  edges: EdgeResponse[],
  setEdges: React.Dispatch<React.SetStateAction<EdgesType[]>>
): EdgesType[] => {
  return edges?.map(edge => ({
    id: edge.edgeId,
    source: edge.sourceNodeId,
    target: edge.targetNodeId,
    type: edge.connectionType === 'loop' ? 'selfLoopEdge' : 'defaultEdge',
    style: {
      strokeWidth: 2,
    },
    data: {
      onDelete: (id: string) => {
        setEdges(prevEdges => prevEdges.filter(e => e.id !== id));
      },
    },
  }));
};
